import React, { useState } from 'react';
import moment, { Moment } from 'moment';

import CalendarEvent from 'flow/components/calendar/CalendarEvent';
import SettingsBlock from 'flow/components/settings/SettingsBlock';
import useAppContext from 'flow/hooks/useAppContext';
import EmptyState from 'flow/pages/messages/EmptyState';
import { Calendar, Event } from 'flow/types/Calendar';
import {
    ChannelKey,
    ContactKey,
    ExtensionKey,
    QueueKey,
    ReferKey,
} from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { groupListBy } from 'flow/utils';
import {
    getFilteredCalendarEvents,
    getRruleUntil,
} from 'flow/utils/calendar-utils';
import Button from '../ui-components/Button';

interface Props {
    entityKey: ChannelKey | ContactKey | ExtensionKey | QueueKey | ReferKey;
    calendar: TypedMap<Calendar>;
}
const CalendarEvents = ({ entityKey, calendar }: Props) => {
    const { i18next } = useAppContext();

    const [startInterval, setStartInterval] = useState(moment().startOf('day'));
    const [endInterval, setEndInterval] = useState(
        moment().add(2, 'month').endOf('day'),
    );

    const events = calendar.get('events');
    const isEditable = calendar.get('editable');

    const expandedEvents = getFilteredCalendarEvents(
        events,
        startInterval,
        endInterval,
    );
    const grouped = groupListBy(expandedEvents, (event: TypedMap<Event>) =>
        moment(event.get('start')).format('YYYY-MM-DD'),
    );
    const sortedKeys = grouped.keySeq().sort();

    const moreEventsExist = () =>
        events.some((event) => {
            const rruleUntil = getRruleUntil(event);
            if (rruleUntil) {
                return (
                    endInterval.toDate().getTime() <
                    moment(rruleUntil).toDate().getTime()
                );
            }

            return (
                endInterval.toDate().getTime() <
                new Date(event.get('end')).getTime()
            );
        });

    return (
        <div>
            {events ? (
                <div className="my-9 flex flex-col gap-9">
                    <div className="block shrink-0 items-center">
                        <SettingsBlock className="flex flex-col gap-y-9">
                            {sortedKeys.map((group: Moment) => (
                                <div
                                    key={'date-key-' + group}
                                    className="flex flex-col gap-1"
                                >
                                    <div className="px-3 text-sm font-bold">
                                        {moment(group).format('Do MMMM YYYY')}
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {grouped
                                            .get(group)
                                            .map(
                                                (
                                                    calendarEvent: TypedMap<Event>,
                                                ) => (
                                                    <CalendarEvent
                                                        key={calendarEvent.get(
                                                            'key',
                                                        )}
                                                        entityKey={entityKey}
                                                        calendarEvent={
                                                            calendarEvent
                                                        }
                                                        editable={
                                                            isEditable
                                                                ? calendarEvent.has(
                                                                      'key',
                                                                  )
                                                                : false
                                                        } //active profile event has no event-key, make it disabled.
                                                    />
                                                ),
                                            )}
                                    </div>
                                </div>
                            ))}
                        </SettingsBlock>
                    </div>
                    {moreEventsExist() && (
                        <Button
                            className="font-xs/5 p-2 font-normal"
                            onClick={() => {
                                const newEndInterval = endInterval
                                    .clone()
                                    .add(1, 'month');
                                setEndInterval(newEndInterval);
                            }}
                            color="ghost"
                        >
                            {i18next.t('calendar.show.more')}
                        </Button>
                    )}
                </div>
            ) : (
                <EmptyState message={i18next.t('calendar.other.events.none')} />
            )}
        </div>
    );
};

export default CalendarEvents;
